import type { ReactNode } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Atmosphere } from '@/components/home/Atmosphere';
import { ThemeSaber } from '@/components/layout/ThemeSaber';
import { isNavGroup, profile, type NavItem } from '@/data/profile';
import { AppLink, useRouter } from '@/lib/router';
import { normalizePath } from '@/lib/paths';

type SiteShellProps = {
  children: ReactNode;
};

function hrefIsCurrent(href: string, path: string): boolean {
  const next = normalizePath(href);
  return path === next || (next !== '/' && path.startsWith(`${next}/`));
}

function itemIsCurrent(item: NavItem, path: string): boolean {
  if (isNavGroup(item)) {
    const onParent = item.href ? hrefIsCurrent(item.href, path) : false;
    return onParent || item.children.some((child) => hrefIsCurrent(child.href, path));
  }
  return hrefIsCurrent(item.href, path);
}

function PrimaryNav() {
  const { path } = useRouter();

  return (
    <Flex as="nav" className="site-nav__links" aria-label="Primary" gap="2" wrap="wrap" justify="flex-end">
      {profile.nav.map((item) => {
        if (isNavGroup(item)) {
          const groupClass = itemIsCurrent(item, path) ? 'nav-group nav-group--current' : 'nav-group';
          const menu = (
            <div className="nav-group__menu">
              {item.children.map((child) => (
                <AppLink key={child.href} href={child.href} className="btn btn--nav" external={child.external}>
                  {child.label}
                </AppLink>
              ))}
            </div>
          );

          if (item.href) {
            return (
              <div key={item.label} className={groupClass}>
                <AppLink href={item.href} className="btn btn--nav">
                  {item.label}
                </AppLink>
                {menu}
              </div>
            );
          }

          return (
            <details key={`${item.label}-${path}`} className={groupClass}>
              <summary className="btn btn--nav">{item.label}</summary>
              {menu}
            </details>
          );
        }

        return (
          <AppLink key={item.href} href={item.href} className="btn btn--nav" external={item.external}>
            {item.label}
          </AppLink>
        );
      })}
    </Flex>
  );
}

export function SiteShell({ children }: SiteShellProps) {
  return (
    <Box className="site" color="fg">
      <Atmosphere />
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Box as="header" position="sticky" top="0" zIndex="10" px={{ base: '4', md: '6' }} pt="3">
        <div className="glass glass--nav site-nav">
          <AppLink href="/" className="brand-link">
            <Text
              as="span"
              fontFamily="heading"
              fontWeight="800"
              letterSpacing="-0.05em"
              textTransform="uppercase"
              fontSize="sm"
            >
              Home
            </Text>
          </AppLink>
          <ThemeSaber />
          <PrimaryNav />
        </div>
      </Box>
      {children}
      <Box as="footer" px={{ base: '5', md: '8' }} py="10">
        <Flex
          className="glass"
          maxW="6xl"
          mx="auto"
          px={{ base: '5', md: '6' }}
          py="5"
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          gap="6"
        >
          <Text fontSize="sm" className="soft">
            © {new Date().getFullYear()} {profile.name} · {profile.location}
          </Text>
          <Flex gap="2" wrap="wrap">
            {profile.links.map((link) => (
              <AppLink key={link.href} href={link.href} className="btn" external={link.external}>
                {link.label}
              </AppLink>
            ))}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
